import type { ReactNode } from 'react'
import { usePermissions } from '@/hooks/usePermissions'
import type { Permission, FamilyRole } from '@/types'

interface PermissionGateProps {
  children: ReactNode
  /** Required permission to render children */
  permission?: Permission
  /** Required role(s) to render children - any match passes */
  roles?: FamilyRole | FamilyRole[]
  /** Render this instead when permission is denied */
  fallback?: ReactNode
  /** If true, requires ALL permissions/roles instead of ANY */
  requireAll?: boolean
}

export function PermissionGate({
  children,
  permission,
  roles,
  fallback = null,
  requireAll = false,
}: PermissionGateProps) {
  const { hasPermission, role: userRole } = usePermissions()

  // Check permission
  const permissionPassed = permission ? hasPermission(permission) : true

  // Check roles
  let rolesPassed = true
  if (roles) {
    const rolesArray = Array.isArray(roles) ? roles : [roles]
    rolesPassed = userRole !== null && rolesArray.includes(userRole)
  }

  // Combine checks based on requireAll flag
  const allowed = requireAll
    ? permissionPassed && rolesPassed
    : permission && roles
      ? permissionPassed || rolesPassed
      : permissionPassed && rolesPassed

  if (!allowed) {
    return <>{fallback}</>
  }

  return <>{children}</>
}

// Convenience component for caregiver-only content
interface CaregiverOnlyProps {
  children: ReactNode
  fallback?: ReactNode
}

export function CaregiverOnly({ children, fallback = null }: CaregiverOnlyProps) {
  return (
    <PermissionGate roles={['PRIMARY_CAREGIVER', 'CAREGIVER']} fallback={fallback}>
      {children}
    </PermissionGate>
  )
}

// Convenience component for parent-only content
interface ParentOnlyProps {
  children: ReactNode
  fallback?: ReactNode
}

export function ParentOnly({ children, fallback = null }: ParentOnlyProps) {
  return (
    <PermissionGate roles="PARENT" fallback={fallback}>
      {children}
    </PermissionGate>
  )
}

// Convenience component for primary caregiver-only content
interface PrimaryCaregiverOnlyProps {
  children: ReactNode
  fallback?: ReactNode
}

export function PrimaryCaregiverOnly({ children, fallback = null }: PrimaryCaregiverOnlyProps) {
  return (
    <PermissionGate roles="PRIMARY_CAREGIVER" fallback={fallback}>
      {children}
    </PermissionGate>
  )
}
